<template>
  <ontos-dialog :model-value="true" max-width="620px" :persistent="loaded" @update:model-value="onDialogInput">
    <v-card>
      <ontos-dialog-loading v-if="!loaded" min-height="160px"/>

      <template v-else-if="blocked">
        <v-card-title><ontos-page-header :title="$tt('delete.blocked-title')" :level="2"/></v-card-title>
        <v-card-text>
          <p v-html="$tt('delete.blocked-intro', {name: identifier})"></p>
          <div v-for="(edge, index) in blockers" :key="index" class="mb-3">
            <div class="text-caption text-grey">
              {{ $tt('delete.link-line', {collection: edge.ideaName, property: edge.propertyName}) }}
            </div>
            <ul class="pl-4">
              <li v-for="item in edge.instances" :key="item._instance">
                <!-- Link e não texto: é por aqui que o usuário vai resolver -->
                <a :href="hrefFor(edge, item)" @click="onBlockerClick($event, edge, item)">{{ item._identifier }}</a>
                <span class="text-grey"> — {{ reasonText(edge, item) }}</span>
              </li>
              <!-- Os que você não vê também bloqueiam, sem serem nomeados -->
              <li v-if="edge.hidden"><ontos-hidden-count :count="edge.hidden"/></li>
            </ul>
          </div>
        </v-card-text>
        <v-row justify="end" class="mt-0 pt-0 pa-5" no-gutters>
          <v-col cols="auto">
            <v-btn size="small" @click="$emit('closed')">
              <v-icon size="small" start>mdi-close</v-icon>
              {{ $tt('close') }}
            </v-btn>
          </v-col>
        </v-row>
      </template>

      <template v-else>
        <v-card-title><ontos-page-header :title="$tt('delete.title', {collection: ideaName})" :level="2"/></v-card-title>
        <v-card-text>
          <p v-html="$tt('delete.message', {name: identifier})"></p>
          <template v-if="links.length">
            <p class="mb-1">{{ $tt('delete.links-intro') }}</p>
            <div v-for="(edge, index) in links" :key="index" class="mb-2">
              <div class="text-caption text-grey">
                {{ $tt('delete.link-line', {collection: edge.ideaName, property: edge.propertyName}) }}
              </div>
              <ul class="pl-4">
                <li v-for="item in edge.instances" :key="item._instance">{{ item._identifier }}</li>
                <li v-if="edge.hidden"><ontos-hidden-count :count="edge.hidden"/></li>
              </ul>
            </div>
          </template>
        </v-card-text>
        <v-row justify="end" class="mt-0 pt-0 pa-5" no-gutters>
          <v-col cols="auto">
            <v-btn size="small" color="error" @click="confirm">
              <v-icon size="small" start>mdi-delete</v-icon>
              {{ $tt('delete.confirm') }}
            </v-btn>
          </v-col>
          <v-col cols="auto" class="pl-2">
            <v-btn size="small" @click="$emit('closed')">
              <v-icon size="small" start>mdi-cancel</v-icon>
              {{ $tt('cancel') }}
            </v-btn>
          </v-col>
        </v-row>
      </template>
    </v-card>
  </ontos-dialog>
</template>

<script lang="ts">
import {Component, Vue, Prop, toNative} from 'vue-facing-decorator'
import DialogLoading from "@/components/shared/DialogLoading.vue";
import HiddenCount from "@/components/shared/HiddenCount.vue";
import {InstanceContext} from "@/types/InstanceContext";
import {resolveInstanceHref} from "@/utils/instanceLink";

/**
 * Confirmação de exclusão com o impacto nos relacionamentos.
 *
 * Busca a análise sozinho a partir do context, então quem hospeda só precisa de um
 * v-if — é o que permite os três caminhos de exclusão usarem o mesmo diálogo.
 *
 * Quem exclui de fato é o host, no `confirmed`: o servidor refaz a checagem de
 * qualquer jeito, então esta tela é conveniência, não garantia.
 */
@Component({emits: ['closed', 'open-instance', 'confirmed'], 
  name: 'ontos-delete-instance-dialog',
  components: {'ontos-dialog-loading': DialogLoading, 'ontos-hidden-count': HiddenCount}
})
class DeleteInstanceDialog extends Vue {
  @Prop({type: Object, required: true}) readonly context!: InstanceContext
  /** Identificador da instância, para a pergunta não ser sobre um número. */
  @Prop({type: String, default: ''}) readonly identifier!: string
  @Prop({type: String, default: ''}) readonly ideaName!: string

  loaded = false
  links: any[] = []
  blockers: any[] = []

  get blocked(): boolean {
    return this.blockers.length > 0
  }

  async mounted() {
    await this.load()
  }

  async load() {
    const {system: systemAlias, idea: ideaAlias, instance} = this.context
    try {
      const res = await this.$instances.dependencies(
          {systemAlias, ideaAlias, instanceId: Number(instance)})
      this.links = res.links || []
      this.blockers = res.blockers || []
    } catch (err) {
      //Sem a prévia não dá para perguntar com honestidade: o host mostra o erro
      this.$emit('showErrorMessage', err.message)
      this.$emit('closed')
      return
    }
    this.loaded = true
  }

  contextFor(edge: any, item: any): InstanceContext {
    return {system: this.context.system, idea: edge.idea, instance: item._instance}
  }

  hrefFor(edge: any, item: any): string {
    return resolveInstanceHref(this.$router, this.contextFor(edge, item))
  }

  /** Mesma convenção dos demais links: ctrl/meio abre a página, clique simples sobe. */
  onBlockerClick(event: MouseEvent, edge: any, item: any) {
    event.stopPropagation()
    if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || (event.button || 0) !== 0) return
    event.preventDefault()
    this.$emit('open-instance', this.contextFor(edge, item))
  }

  reasonText(edge: any, item: any): string {
    const values = {property: edge.propertyName, min: item.min, after: item.after}
    if (item.reason === 'required') return this.$tt('delete.reason-required', values) as string
    if (item.reason === 'identifier') return this.$tt('delete.reason-identifier', values) as string
    return this.$tt('delete.reason-min-items', values) as string
  }

  confirm() {
    this.$emit('confirmed')
  }

  //Enquanto carrega a janela é descartável; com a pergunta na tela, não
  onDialogInput(open: boolean) {
    if (!open && !this.loaded) this.$emit('closed')
  }
}

export default toNative(DeleteInstanceDialog)
</script>
